<!-- components/DynamicForm.vue -->
<script lang="ts">
import type { FormItemRule, SelectOption, TreeSelectOption } from "naive-ui";
import type { VNodeChild } from "vue";

export type FormFieldType = "text" | "password" | "number" | "select" | "select-multiple" | "tree-select" | "autocomplete" | "date" | "textarea" | "switch" | "checkbox" | "dynamic-input" | "input-pair" | "title";

export interface FormFieldOption {
  label: string;
  value: string | number;
}

export interface FormField {
  name?: string;
  label?: string;
  checkboxLabel?: string;
  type?: FormFieldType;
  options?: SelectOption[] | TreeSelectOption[] | ((inputValue: string) => SelectOption[]) | any;
  required?: boolean;
  rule?: FormItemRule | Array<FormItemRule>;
  hidden?: boolean | ((model: FormModel, index?: number) => boolean);
  disabled?: boolean | ((model: FormModel, index?: number) => boolean);
  itemFields?: FormFieldItem[];
  itemDefault?: () => Record<string, any>;
  min?: number | ((model: FormModel) => number);
  max?: number | ((model: FormModel) => number);
  /** Khusus input-pair: teks pemisah antara kedua input (default "to"). */
  inputPairSeparator?: string;
  /** Khusus input-pair: nama ikon (iconify) untuk suffix di kanan input. */
  suffixIcon?: string;
  maxlength?: number;
  showCount?: boolean;
  rows?: number;
  createButtonLabel?: string;
  showAction?: boolean | "last";
  /** Khusus tree-select: kalau true (default), klik node yang punya children hanya expand/collapse, jadi hanya leaf yang bisa dipilih. */
  leafOnly?: boolean;
  /** Khusus select / select-multiple: custom render label opsi (dan label tag yang terpilih). */
  renderLabel?: (option: SelectOption, selected: boolean) => VNodeChild;
  onUpdateValue?: (value: any) => void;
}

export type FormFieldItem = FormField | FormField[];
export type FormModel = Record<string, any>;
</script>

<script setup lang="ts">
import type { TreeSelectOverrideNodeClickBehavior } from "naive-ui";
import { Icon } from "@iconify/vue";
import { NAutoComplete, NCheckbox, NDatePicker, NDynamicInput, NFormItem, NIcon, NInput, NInputNumber, NSelect, NSwitch, NTreeSelect } from "naive-ui";
import { computed, unref } from "vue";

defineOptions({ name: "DynamicForm" });

const props = withDefaults(
  defineProps<{
    fields: FormFieldItem[];
    bare?: boolean;
    /** Index item ini di dalam parent dynamic-input (kalau komponen ini di-render sebagai itemFields). */
    itemIndex?: number;
  }>(),
  {
    bare: false,
  },
);

const model = defineModel<FormModel>("value", { default: () => ({}) });

function isHidden(field: FormField): boolean {
  return typeof field.hidden === "function" ? field.hidden(model.value, props.itemIndex) : !!field.hidden;
}

function isDisabled(field: FormField): boolean {
  return typeof field.disabled === "function" ? field.disabled(model.value, props.itemIndex) : !!field.disabled;
}

function getMin(field: FormField): number | undefined {
  return typeof field.min === "function" ? field.min(model.value) : field.min;
}

function getMax(field: FormField): number | undefined {
  return typeof field.max === "function" ? field.max(model.value) : field.max;
}

const rows = computed(() => {
  return props.fields.map((item) => (Array.isArray(item) ? item.filter((f) => !isHidden(f)) : item)).filter((item) => (Array.isArray(item) ? item.length > 0 : !isHidden(item)));
});

function getPath(obj: any, path: string): any {
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

function setPath(obj: Record<string, any>, path: string, value: any) {
  const keys = path.split(".");
  let cur = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] == null || typeof cur[k] !== "object") cur[k] = {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
}

function getValue(field: FormField) {
  if (!field.name) return undefined;
  const value = getPath(model.value, field.name);
  if (field.type === "select-multiple") {
    return Array.isArray(value) ? value : [];
  }
  if (field.type === "switch" || field.type === "checkbox") {
    return !!value;
  }
  if (field.type === "dynamic-input") {
    return Array.isArray(value) ? value : [];
  }
  if (field.type === "input-pair") {
    // n-input pair memakai array [begin, end] sebagai value.
    const pair = (value ?? []) as string[];
    return [pair[0] ?? "", pair[1] ?? ""];
  }
  return value;
}

function setValue(field: FormField, value: any) {
  if (!field.name) return;
  setPath(model.value, field.name, value);
}

interface AutoCompleteOptionItem {
  label: string;
  value: string;
}

function resolveOptions(field: FormField): AutoCompleteOptionItem[] {
  const raw = typeof field.options === "function" ? (field.options as (v: string) => SelectOption[])((getValue(field) as string) ?? "") : field.options;
  const list = (unref(raw) as SelectOption[]) || [];
  return list.map((opt) => ({ label: String(opt.label), value: String(opt.value) }));
}

function handleAutocompleteUpdate(field: FormField, value: string) {
  setValue(field, value);
  field.onUpdateValue?.(value);
}

function handleCheckboxUpdate(field: FormField, value: boolean) {
  setValue(field, value);
  field.onUpdateValue?.(value);
}

function handleTreeSelectUpdate(field: FormField, value: any) {
  setValue(field, value);
  field.onUpdateValue?.(value);
}

// tree-select: node yang punya children hanya toggle expand (hanya leaf yang bisa dipilih)
function getTreeOverride(field: FormField): TreeSelectOverrideNodeClickBehavior {
  return ({ option }) => {
    if (field.leafOnly !== false && Array.isArray(option.children) && option.children.length > 0) {
      return "toggleExpand";
    }
    return "default";
  };
}

function createItem(field: FormField) {
  return field.itemDefault ? field.itemDefault() : {};
}

const gridColsClass: Record<number, string> = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};

function colsClass(n: number): string {
  return gridColsClass[Math.min(n, 4)] ?? "md:grid-cols-4";
}

function isRequired(field: FormField): boolean {
  return !!field.required || (Array.isArray(field.rule) ? field.rule.some((x) => !!x.required) : !!field.rule?.required);
}

function showCount(field: FormField): boolean {
  return field.maxlength != null && field.showCount !== false;
}
</script>

<template>
  <template v-for="(item, idx) in rows" :key="idx">
    <!-- Grid Row (Array of Fields) -->
    <div v-if="Array.isArray(item)" class="grid grid-cols-1 gap-x-3 gap-y-3 mb-3" :class="colsClass(item.length)">
      <template v-for="field in item" :key="field.name || field.label">
        <div v-if="field.type === 'title'" class="text-lg font-medium text-gray-800 mb-2 col-span-full">
          {{ field.label }}
        </div>

        <!-- Mode bare: tanpa n-form-item -->
        <div v-else-if="bare" class="flex flex-col">
          <div v-if="field.label && field.type !== 'checkbox'" class="text-xs text-gray-600 mb-1 font-normal">{{ field.label }}<span v-if="isRequired(field)" class="text-red-500"> *</span></div>

          <n-input
            v-if="field.type === 'text'"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
          />
          <n-auto-complete
            v-else-if="field.type === 'autocomplete'"
            :value="getValue(field) as any"
            :options="resolveOptions(field) as any"
            :disabled="isDisabled(field)"
            :input-props="{ autocomplete: 'disabled' }"
            clearable
            @update:value="(v) => handleAutocompleteUpdate(field, v)"
          />
          <n-input
            v-else-if="field.type === 'password'"
            type="password"
            show-password-on="mousedown"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
          />
          <n-input
            v-else-if="field.type === 'textarea'"
            type="textarea"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
            :rows="field.rows"
          />
          <n-input-number v-else-if="field.type === 'number'" :value="getValue(field) as any" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)" placeholder="" class="w-full" />
          <n-input v-else-if="field.type === 'input-pair'" pair :separator="field.inputPairSeparator ?? 'to'" clearable :value="getValue(field) as any" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)">
            <template v-if="field.suffixIcon" #suffix>
              <n-icon><Icon :icon="field.suffixIcon" /></n-icon>
            </template>
          </n-input>
          <n-select
            v-else-if="field.type === 'select'"
            :value="getValue(field) as string | number | null"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :render-label="field.renderLabel"
            placeholder=""
            @update:value="(v) => setValue(field, v)"
            clearable
            filterable
          />
          <n-select
            v-else-if="field.type === 'select-multiple'"
            :value="getValue(field) as any"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :render-label="field.renderLabel"
            multiple
            placeholder=""
            @update:value="(v) => setValue(field, v)"
            clearable
            filterable
          />
          <n-tree-select
            v-else-if="field.type === 'tree-select'"
            :value="getValue(field) as any"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :override-default-node-click-behavior="getTreeOverride(field)"
            placeholder=""
            block-line
            default-expand-all
            show-line
            show-path
            clearable
            filterable
            @update:value="(v) => handleTreeSelectUpdate(field, v)"
          />
          <n-date-picker
            v-else-if="field.type === 'date'"
            :formatted-value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:formatted-value="(v) => setValue(field, v)"
            value-format="yyyy-MM-dd"
            type="date"
            clearable
            placeholder=""
            class="w-full"
          />

          <div v-else-if="field.type === 'switch'" class="">
            <n-switch :value="getValue(field) as boolean" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)" />
          </div>
          <n-checkbox v-else-if="field.type === 'checkbox'" :checked="getValue(field) as boolean" :disabled="isDisabled(field)" @update:checked="(v) => handleCheckboxUpdate(field, v)">
            {{ field.checkboxLabel || field.label }}
          </n-checkbox>
          <n-dynamic-input
            v-else-if="field.type === 'dynamic-input'"
            :value="getValue(field) as any[]"
            @update:value="(v) => setValue(field, v)"
            :min="getMin(field)"
            :max="getMax(field)"
            :disabled="isDisabled(field)"
            :show-action-button="field.showAction ?? true"
            :on-create="() => createItem(field)"
            :class="{ 'show-action-last-only': field.showAction === 'last' }"
          >
            <template #create-button-default>
              {{ field.createButtonLabel || "Add" }}
            </template>
            <template #default="{ value: itemValue, index: itemIdx }">
              <div class="flex-1 min-w-0">
                <DynamicForm :fields="field.itemFields || []" :value="itemValue" :item-index="itemIdx" bare />
              </div>
            </template>
          </n-dynamic-input>
        </div>

        <!-- Mode normal: pakai n-form-item -->
        <n-form-item v-else :path="field.name" :label="field.type === 'checkbox' ? undefined : field.label" :required="isRequired(field)">
          <n-input
            v-if="field.type === 'text'"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
          />
          <n-auto-complete
            v-else-if="field.type === 'autocomplete'"
            :value="getValue(field) as any"
            :options="resolveOptions(field) as any"
            :disabled="isDisabled(field)"
            :input-props="{ autocomplete: 'disabled' }"
            placeholder=""
            clearable
            @update:value="(v) => handleAutocompleteUpdate(field, v)"
          />
          <n-input
            v-else-if="field.type === 'password'"
            type="password"
            show-password-on="mousedown"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
          />
          <n-input
            v-else-if="field.type === 'textarea'"
            type="textarea"
            :value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:value="(v) => setValue(field, v)"
            placeholder=""
            :maxlength="field.maxlength"
            :show-count="showCount(field)"
            :rows="field.rows"
          />
          <n-input-number v-else-if="field.type === 'number'" :value="getValue(field) as any" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)" placeholder="" class="w-full" />
          <n-input v-else-if="field.type === 'input-pair'" pair :separator="field.inputPairSeparator ?? 'to'" clearable :value="getValue(field) as any" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)">
            <template v-if="field.suffixIcon" #suffix>
              <n-icon><Icon :icon="field.suffixIcon" /></n-icon>
            </template>
          </n-input>
          <n-select
            v-else-if="field.type === 'select'"
            :value="getValue(field) as string | number | null"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :render-label="field.renderLabel"
            placeholder=""
            @update:value="(v) => setValue(field, v)"
            clearable
            filterable
          />
          <n-select
            v-else-if="field.type === 'select-multiple'"
            :value="getValue(field) as any"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :render-label="field.renderLabel"
            multiple
            placeholder=""
            @update:value="(v) => setValue(field, v)"
            clearable
            filterable
          />
          <n-tree-select
            v-else-if="field.type === 'tree-select'"
            :value="getValue(field) as any"
            :options="field.options || []"
            :disabled="isDisabled(field)"
            :override-default-node-click-behavior="getTreeOverride(field)"
            placeholder=""
            block-line
            default-expand-all
            show-line
            show-path
            clearable
            filterable
            @update:value="(v) => handleTreeSelectUpdate(field, v)"
          />
          <n-date-picker
            v-else-if="field.type === 'date'"
            :formatted-value="getValue(field) as any"
            :disabled="isDisabled(field)"
            @update:formatted-value="(v) => setValue(field, v)"
            value-format="yyyy-MM-dd"
            type="date"
            clearable
            placeholder=""
            class="w-full"
          />
          <n-switch v-else-if="field.type === 'switch'" :value="getValue(field) as boolean" :disabled="isDisabled(field)" @update:value="(v) => setValue(field, v)" />
          <n-checkbox v-else-if="field.type === 'checkbox'" :checked="getValue(field) as boolean" :disabled="isDisabled(field)" @update:checked="(v) => handleCheckboxUpdate(field, v)">
            {{ field.checkboxLabel || field.label }}
          </n-checkbox>
          <n-dynamic-input
            v-else-if="field.type === 'dynamic-input'"
            :value="getValue(field) as any[]"
            @update:value="(v) => setValue(field, v)"
            :min="getMin(field)"
            :max="getMax(field)"
            :disabled="isDisabled(field)"
            :show-action-button="field.showAction ?? true"
            :on-create="() => createItem(field)"
            :class="{ 'show-action-last-only': field.showAction === 'last' }"
          >
            <template #create-button-default>
              {{ field.createButtonLabel || "Add" }}
            </template>
            <template #default="{ value: itemValue, index: itemIdx }">
              <div class="flex-1 min-w-0">
                <DynamicForm :fields="field.itemFields || []" :value="itemValue" :item-index="itemIdx" bare />
              </div>
            </template>
          </n-dynamic-input>
        </n-form-item>
      </template>
    </div>

    <!-- Single Item Row -->
    <div v-else-if="item.type === 'title'" class="text-lg font-medium text-gray-800 mb-2">
      {{ item.label }}
    </div>

    <!-- Mode bare: tanpa n-form-item -->
    <div v-else-if="bare" class="mb-3">
      <div v-if="item.label && item.type !== 'checkbox'" class="text-xs text-gray-600 mb-1 font-normal">{{ item.label }}<span v-if="isRequired(item)" class="text-red-500"> *</span></div>
      <n-input
        v-if="item.type === 'text'"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
      />
      <n-auto-complete
        v-else-if="item.type === 'autocomplete'"
        :value="getValue(item) as any"
        :options="resolveOptions(item) as any"
        :disabled="isDisabled(item)"
        :input-props="{ autocomplete: 'disabled' }"
        placeholder=""
        clearable
        @update:value="(v) => handleAutocompleteUpdate(item, v)"
      />
      <n-input
        v-else-if="item.type === 'password'"
        type="password"
        show-password-on="mousedown"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
      />
      <n-input
        v-else-if="item.type === 'textarea'"
        type="textarea"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
        :rows="item.rows"
      />
      <n-input-number v-else-if="item.type === 'number'" :value="getValue(item) as any" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)" placeholder="" class="w-full" />
      <n-input v-else-if="item.type === 'input-pair'" pair :separator="item.inputPairSeparator ?? 'to'" clearable :value="getValue(item) as any" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)">
        <template v-if="item.suffixIcon" #suffix>
          <n-icon><Icon :icon="item.suffixIcon" /></n-icon>
        </template>
      </n-input>
      <n-select
        v-else-if="item.type === 'select'"
        :value="getValue(item) as string | number | null"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :render-label="item.renderLabel"
        placeholder=""
        @update:value="(v) => setValue(item, v)"
        clearable
        filterable
      />
      <n-select
        v-else-if="item.type === 'select-multiple'"
        :value="getValue(item) as any"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :render-label="item.renderLabel"
        multiple
        placeholder=""
        @update:value="(v) => setValue(item, v)"
        clearable
        filterable
      />
      <n-tree-select
        v-else-if="item.type === 'tree-select'"
        :value="getValue(item) as any"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :override-default-node-click-behavior="getTreeOverride(item)"
        placeholder=""
        block-line
        default-expand-all
        show-line
        show-path
        clearable
        filterable
        @update:value="(v) => handleTreeSelectUpdate(item, v)"
      />
      <n-date-picker
        v-else-if="item.type === 'date'"
        :formatted-value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:formatted-value="(v) => setValue(item, v)"
        value-format="yyyy-MM-dd"
        type="date"
        clearable
        placeholder=""
        class="w-full"
      />
      <n-switch v-else-if="item.type === 'switch'" :value="getValue(item) as boolean" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)" />
      <n-checkbox v-else-if="item.type === 'checkbox'" :checked="getValue(item) as boolean" :disabled="isDisabled(item)" @update:checked="(v) => handleCheckboxUpdate(item, v)">
        {{ item.checkboxLabel || item.label }}
      </n-checkbox>
      <n-dynamic-input
        v-else-if="item.type === 'dynamic-input'"
        :value="getValue(item) as any[]"
        @update:value="(v) => setValue(item, v)"
        :min="getMin(item)"
        :max="getMax(item)"
        :disabled="isDisabled(item)"
        :show-action-button="item.showAction ?? true"
        :on-create="() => createItem(item)"
        :class="{ 'show-action-last-only': item.showAction === 'last' }"
      >
        <template #create-button-default>
          {{ item.createButtonLabel || "Add" }}
        </template>
        <template #default="{ value: itemValue, index: itemIdx }">
          <div class="flex-1 min-w-0">
            <DynamicForm :fields="item.itemFields || []" :value="itemValue" :item-index="itemIdx" bare />
          </div>
        </template>
      </n-dynamic-input>
    </div>

    <!-- Mode normal: pakai n-form-item -->
    <n-form-item v-else :path="item.name" :label="item.type === 'checkbox' ? undefined : item.label" :required="isRequired(item)">
      <n-input
        v-if="item.type === 'text'"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
      />
      <n-auto-complete
        v-else-if="item.type === 'autocomplete'"
        :value="getValue(item) as any"
        :options="resolveOptions(item) as any"
        :disabled="isDisabled(item)"
        :input-props="{ autocomplete: 'disabled' }"
        clearable
        placeholder=""
        @update:value="(v) => handleAutocompleteUpdate(item, v)"
      />
      <n-input
        v-else-if="item.type === 'password'"
        type="password"
        show-password-on="mousedown"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
      />
      <n-input
        v-else-if="item.type === 'textarea'"
        type="textarea"
        :value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:value="(v) => setValue(item, v)"
        placeholder=""
        :maxlength="item.maxlength"
        :show-count="showCount(item)"
        :rows="item.rows"
      />
      <n-input-number v-else-if="item.type === 'number'" :value="getValue(item) as any" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)" placeholder="" class="w-full" />
      <n-input v-else-if="item.type === 'input-pair'" pair :separator="item.inputPairSeparator ?? 'to'" clearable :value="getValue(item) as any" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)">
        <template v-if="item.suffixIcon" #suffix>
          <n-icon><Icon :icon="item.suffixIcon" /></n-icon>
        </template>
      </n-input>
      <n-select
        v-else-if="item.type === 'select'"
        :value="getValue(item) as string | number | null"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :render-label="item.renderLabel"
        placeholder=""
        @update:value="(v) => setValue(item, v)"
        clearable
        filterable
      />
      <n-select
        v-else-if="item.type === 'select-multiple'"
        :value="getValue(item) as any"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :render-label="item.renderLabel"
        multiple
        placeholder=""
        @update:value="(v) => setValue(item, v)"
        clearable
        filterable
      />
      <n-tree-select
        v-else-if="item.type === 'tree-select'"
        :value="getValue(item) as any"
        :options="item.options || []"
        :disabled="isDisabled(item)"
        :override-default-node-click-behavior="getTreeOverride(item)"
        placeholder=""
        block-line
        default-expand-all
        show-line
        show-path
        clearable
        filterable
        @update:value="(v) => handleTreeSelectUpdate(item, v)"
      />
      <n-date-picker
        v-else-if="item.type === 'date'"
        :formatted-value="getValue(item) as any"
        :disabled="isDisabled(item)"
        @update:formatted-value="(v) => setValue(item, v)"
        value-format="yyyy-MM-dd"
        type="date"
        clearable
        placeholder=""
        class="w-full"
      />
      <n-switch v-else-if="item.type === 'switch'" :value="getValue(item) as boolean" :disabled="isDisabled(item)" @update:value="(v) => setValue(item, v)" />
      <n-checkbox v-else-if="item.type === 'checkbox'" :checked="getValue(item) as boolean" :disabled="isDisabled(item)" @update:checked="(v) => handleCheckboxUpdate(item, v)">
        {{ item.checkboxLabel || item.label }}
      </n-checkbox>
      <n-dynamic-input
        v-else-if="item.type === 'dynamic-input'"
        :value="getValue(item) as any[]"
        @update:value="(v) => setValue(item, v)"
        :min="getMin(item)"
        :max="getMax(item)"
        :disabled="isDisabled(item)"
        :show-action-button="item.showAction ?? true"
        :on-create="() => createItem(item)"
        :class="{ 'show-action-last-only': item.showAction === 'last' }"
      >
        <template #create-button-default>
          {{ item.createButtonLabel || "Add" }}
        </template>
        <template #default="{ value: itemValue, index: itemIdx }">
          <div class="flex-1 min-w-0">
            <DynamicForm :fields="item.itemFields || []" :value="itemValue" :item-index="itemIdx" bare />
          </div>
        </template>
      </n-dynamic-input>
    </n-form-item>
  </template>
</template>

<style scoped>
:deep(.n-dynamic-input-item) {
  align-items: center !important;
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px dashed rgb(239, 239, 245);
}

:deep(.n-dynamic-input-item:last-child) {
  border-bottom: none;
  padding-bottom: 0;
}

:deep(.n-dynamic-input-item__action) {
  align-self: center !important;
  margin-left: 0.75rem;
}

:deep(.show-action-last-only .n-dynamic-input-item:not(:last-child) .n-dynamic-input-item__action) {
  display: none !important;
}
</style>
